/** FPS counter overlay — Accessibility → Show FPS. */

export type FpsShowWhere = "home" | "game" | "always";

const FPS_SAMPLE_MS = 500;

let enabled = false;
let where: FpsShowWhere = "game";
let frameCount = 0;
let sampleStartAt = 0;
let lastDomUpdateAt = 0;
let displayedFps = 0;
let root: HTMLElement | null = null;
let valueEl: HTMLElement | null = null;

function fpsShouldShow(): boolean {
    if (!enabled) return false;
    if (where === "always") return true;
    const inGame = document.body.classList.contains("game-active");
    if (where === "game") return inGame;
    return !inGame;
}

function ensureRoot() {
    if (root) return;
    root = document.getElementById("fpsOverlay");
    if (!root) {
        root = document.createElement("div");
        root.id = "fpsOverlay";
        root.className = "fps-overlay hidden";
        root.setAttribute("aria-hidden", "true");
        root.innerHTML =
            '<span class="fps-overlay-prefix">FPS</span><span class="fps-overlay-sep" aria-hidden="true">:</span><span class="fps-overlay-value">0</span>';
        document.body.appendChild(root);
    }
    valueEl = root.querySelector(".fps-overlay-value");
}

function syncPlacementClass() {
    ensureRoot();
    if (!root) return;
    const inGame = document.body.classList.contains("game-active");
    root.classList.toggle("fps-overlay--inGame", inGame);
    root.classList.toggle("fps-overlay--menu", !inGame);
}

function syncLabel() {
    ensureRoot();
    if (valueEl) valueEl.textContent = String(displayedFps);
}

function syncVisibility() {
    ensureRoot();
    if (!root) return;
    const on = fpsShouldShow();
    root.classList.toggle("hidden", !on);
    root.setAttribute("aria-hidden", on ? "false" : "true");
    if (on) syncPlacementClass();
}

/** No-op — FPS uses fixed CSS placement (not anchored to Return/Pause). */
export function scheduleFpsOverlayPosition() {
    syncPlacementClass();
}

export function syncFpsOverlayPosition() {
    syncPlacementClass();
}

export function mountFpsOverlay() {
    ensureRoot();
    syncPlacementClass();
    syncLabel();
    syncVisibility();
}

export function setFpsOverlayOptions(opts: { enabled: boolean; where: FpsShowWhere }) {
    enabled = opts.enabled;
    where = opts.where;
    frameCount = 0;
    sampleStartAt = performance.now();
    lastDomUpdateAt = 0;
    syncVisibility();
    syncLabel();
}

export function noteFpsFrame(now: number) {
    if (!enabled) return;
    if (sampleStartAt <= 0) sampleStartAt = now;
    frameCount += 1;

    const show = fpsShouldShow();
    ensureRoot();
    if (root) {
        const wasHidden = root.classList.contains("hidden");
        syncVisibility();
        if (show && wasHidden) {
            frameCount = 1;
            sampleStartAt = now;
            lastDomUpdateAt = 0;
        }
    }

    if (!show) return;

    if (lastDomUpdateAt <= 0) lastDomUpdateAt = now;
    if (now - lastDomUpdateAt < FPS_SAMPLE_MS) return;

    const elapsedSec = (now - sampleStartAt) / 1000;
    if (elapsedSec > 0) {
        displayedFps = Math.max(0, Math.round(frameCount / elapsedSec));
    }
    frameCount = 0;
    sampleStartAt = now;
    lastDomUpdateAt = now;
    syncLabel();
}

export function readFpsShowWhere(raw: string | null | undefined): FpsShowWhere {
    if (raw === "home" || raw === "always") return raw;
    return "game";
}
